// Shared between the seller expenses page (CSV download) and the server
// email-CSV route so the spreadsheet layout and category labels never
// diverge between the two export surfaces.

export interface ExpenseCategory {
  value: string;
  label: string;
}

export const EXPENSE_CATEGORIES: ExpenseCategory[] = [
  { value: "seeds", label: "Seeds" },
  { value: "plants_seedlings", label: "Plants / seedlings" },
  { value: "soil_compost", label: "Soil / compost" },
  { value: "fertilizer", label: "Fertilizer / amendments" },
  { value: "pots_containers", label: "Pots / containers" },
  { value: "tools_equipment", label: "Tools / equipment" },
  { value: "packaging", label: "Packaging / jars" },
  { value: "ingredients", label: "Ingredients / supplies" },
  { value: "water_utilities", label: "Water / utilities" },
  { value: "mileage_fuel", label: "Mileage / fuel" },
  { value: "market_fees", label: "Market / booth fees" },
  { value: "other", label: "Other" },
];

export function expenseCategoryLabel(value: string): string {
  return EXPENSE_CATEGORIES.find((c) => c.value === value)?.label ?? value;
}

export const MILEAGE_CATEGORY = "mileage_fuel";

// IRS business standard mileage rate, 2026 (Notice 2026-10): 72.5¢/mile.
// Bump this each January when the IRS publishes the new rate.
export const IRS_MILEAGE_RATE = 0.725;

export function mileageDeduction(miles: number): number {
  return Math.round(miles * IRS_MILEAGE_RATE * 100) / 100;
}

export interface ExpenseCsvRow {
  expenseDate: string;
  category: string;
  vendor: string | null;
  amount: string | number;
  miles: string | number | null;
  notes: string | null;
  receiptImageUrl: string | null;
}

function csvCell(value: string): string {
  if (/[",\n]/.test(value)) return `"${value.replace(/"/g, '""')}"`;
  return value;
}

export function buildExpensesCsv(
  rows: ExpenseCsvRow[],
  salesIncome: number,
  totalExpenses: number,
): string {
  const net = salesIncome - totalExpenses;
  const header = ["Date", "Category", "Vendor", "Amount", "Miles", "Notes", "Receipt"];
  const body = rows.map((e) => [
    e.expenseDate,
    expenseCategoryLabel(e.category),
    e.vendor ?? "",
    Number(e.amount).toFixed(2),
    e.miles != null && e.miles !== "" ? Number(e.miles).toFixed(1) : "",
    e.notes ?? "",
    e.receiptImageUrl ?? "",
  ]);
  const summary = [
    [],
    ["Stoop sales income (est.)", "", "", salesIncome.toFixed(2)],
    ["Total expenses", "", "", totalExpenses.toFixed(2)],
    ["Net (estimated taxable)", "", "", net.toFixed(2)],
    [`Mileage deduction uses the ${IRS_MILEAGE_RATE * 100}¢/mile IRS rate`],
  ];
  return [header, ...body, ...summary]
    .map((cols) => cols.map((c) => csvCell(String(c))).join(","))
    .join("\n");
}
